import { QrCode, Image } from "lucide-react";

const SAMPLE_DATA = {
  nombre_cliente: "Maria Garcia",
  matricula: "1234 ABC",
  telefono: "600 123 456",
  email: "maria@ejemplo.com",
  fecha_emision: "2026-01-15",
  fecha_caducidad: "2026-03-15",
  centro: "Ascauto Madrid",
  asesor: "Carlos Ruiz",
  codigo_bono: "ASC-ATC-2026-042",
  observaciones: "Lavado completo",
  valor_estimado: "50 EUR",
  tipo_bono: "BONO LAVADO",
};

export function LayoutThumbnail({ layout, barColor, accentColor, className = "" }) {
  if (!layout || !layout.elements || layout.elements.length === 0) {
    return (
      <div className={`relative overflow-hidden bg-slate-50 ${className}`} style={{ aspectRatio: "595/842" }} data-testid="tpl-thumbnail-fallback">
        <div className="absolute inset-0 flex flex-col">
          <div className="h-[10%]" style={{ backgroundColor: barColor || "#0f172a" }} />
          <div className="h-[0.6%]" style={{ backgroundColor: accentColor || "#dc2626" }} />
          <div className="flex-1 p-[8%] space-y-[4%]">
            <div className="h-[3%] w-[55%] rounded-[1px]" style={{ backgroundColor: barColor || "#0f172a", opacity: 0.8 }} />
            <div className="h-[1.5%] w-[40%] bg-slate-200 rounded-[1px]" />
            <div className="h-[0.5%] w-full bg-slate-100 mt-[2%]" />
            <div className="grid grid-cols-2 gap-[3%] mt-[2%]">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-[2px]">
                  <div className="h-[2px] w-[60%] bg-slate-200 rounded-[1px]" />
                  <div className="h-[3px] w-[80%] bg-slate-300 rounded-[1px]" />
                </div>
              ))}
            </div>
          </div>
          <div className="h-[0.6%]" style={{ backgroundColor: accentColor || "#dc2626" }} />
          <div className="h-[6%]" style={{ backgroundColor: barColor || "#0f172a" }} />
        </div>
      </div>
    );
  }

  const sorted = [...layout.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  return (
    <div className={`relative overflow-hidden ${className}`}
      style={{ aspectRatio: "595/842", backgroundColor: layout.pageBackground || "#f1f5f9" }}
      data-testid="tpl-thumbnail-layout">
      {sorted.map((el, idx) => (
        <ThumbnailElement key={el.id || idx} el={el} />
      ))}
    </div>
  );
}

function ThumbnailElement({ el }) {
  const base = {
    position: "absolute",
    left: `${el.x}%`,
    top: `${el.y}%`,
    width: `${el.width}%`,
    height: `${el.height}%`,
    zIndex: el.zIndex || 0,
    opacity: el.opacity ?? 1,
    overflow: "hidden",
  };

  if (el.type === "shape") {
    if (el.shape === "line") {
      return (
        <div style={{ ...base, display: "flex", alignItems: "center" }}>
          <div style={{ width: "100%", borderTop: `1px solid ${el.color || "#e2e8f0"}` }} />
        </div>
      );
    }
    return (
      <div style={{
        ...base,
        backgroundColor: el.color || "#e2e8f0",
        borderRadius: el.borderRadius ? Math.min(el.borderRadius, 4) : 0,
      }} />
    );
  }

  if (el.type === "text") {
    return (
      <div style={{
        ...base,
        fontSize: Math.max(2, (el.fontSize || 14) * 0.14),
        fontWeight: el.fontWeight || "normal",
        fontStyle: el.fontStyle || "normal",
        color: el.color || "#000",
        textAlign: el.textAlign || "left",
        lineHeight: 1.15,
        padding: "0 1px",
        whiteSpace: "nowrap",
      }}>
        {el.text || ""}
      </div>
    );
  }

  if (el.type === "dynamic_field") {
    const value = SAMPLE_DATA[el.field] || "";
    const labelFs = Math.max(1.5, (el.fontSize || 14) * 0.08);
    const valueFs = Math.max(2, (el.fontSize || 14) * 0.14);
    return (
      <div style={{ ...base, padding: "0 1px" }}>
        {el.label && (
          <div style={{
            fontSize: labelFs,
            fontWeight: "bold",
            color: el.labelColor || "#94a3b8",
            lineHeight: 1.1,
            letterSpacing: "0.2px",
          }}>{el.label}</div>
        )}
        <div style={{
          fontSize: valueFs,
          fontWeight: el.fontWeight || "normal",
          color: el.color || "#000",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}>{value}</div>
      </div>
    );
  }

  if (el.type === "qr") {
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <QrCode style={{ width: "70%", height: "70%", color: "#475569" }} />
      </div>
    );
  }

  if (el.type === "image") {
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image style={{ width: "50%", height: "50%", color: "#94a3b8", opacity: 0.5 }} />
      </div>
    );
  }

  return null;
}
